import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Button, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  mergeDivisionSchedule, deriveUpcomingGames, deriveNeedsResult, deriveResultsByDate, deriveScheduledByDate,
  deriveDivisionTeamRecordsFromRows, sortTeamRecordsForDisplay, describeEventScope, filterRowsForTeam,
} from '../../utils/divisionSchedule';
import { todayDateString, EVENT_TYPE_LABELS, formatTimeCompact } from '../../utils/teamSchedule';
import {
  gameStatusChipStyle, deriveGameResult, formatGameOpponentLine, formatGameDateCompact,
  formatResultDateLabel, formatScheduleDayLabel,
} from '../../utils/gameResult';
import { divisionSecondaryLabel, competitionTypeLabel, competitionTypeChipStyle } from '../../utils/divisionDisplay';
import { EventActionsMenu } from '../teams/GameFormDialogs';

// Phase C.4a — "THE DIVISION IS THE HUB." This is the ONE shared
// presentation both the public Division page (PublicDivisionPage.js)
// and the authenticated Division management surface
// (DivisionSchedulePage.js) render — authority changes what a viewer
// can SEE and DO, never which component renders it. Security still
// lives entirely upstream of this file: a public caller must only ever
// pass in public-safe data (no Practice/Other, no unpublished-Team
// external games) fetched through the existing public-safe RPCs — this
// component has no knowledge of auth at all beyond the plain `canManage`
// boolean that gates whether row-action buttons render. It never
// fetches anything itself and never decides what's safe to show; it
// only lays out and formats whatever `perTeam` it's handed. Reuse
// presentation, not permissions.
//
// `perTeam`: Array<{ team: {id, team_name, public_page_enabled?},
// events: array, matchups: array }> — one entry per Division Team.
// `events` for the authenticated caller includes Game+Practice+Other;
// the public caller passes only what its public RPCs actually returned
// (Games only — no public RPC in this codebase returns Practice/Other,
// so there is nothing to filter here, only to never fetch upstream).

function HubEventRow({ row, canManage, getTeamHref, teamNamesById, onEdit, onComplete, onPostpone, onCancel, onDelete, onReopen, highlighted }) {
  const isCancelled = row.status === 'cancelled';
  const isCompleted = row.status === 'completed';
  const isGame = row.event_type === 'game';
  const result = isCompleted ? deriveGameResult(row.team_score, row.opponent_score) : null;
  const heading = isGame ? `${row.ownerTeamName} ${formatGameOpponentLine(row)}` : (row.title || EVENT_TYPE_LABELS[row.event_type] || 'Event');
  const href = getTeamHref ? getTeamHref({ id: row.ownerTeamId, team_name: row.ownerTeamName }) : null;

  return (
    <Card sx={{
      backgroundColor: highlighted ? 'rgba(46,168,75,0.06)' : 'rgba(10,20,34,0.8)',
      border: highlighted ? '1px solid rgba(46,168,75,0.35)' : '1px solid #1e2a3a',
      opacity: isCancelled ? 0.65 : 1,
    }}>
      <CardContent sx={{ py: 1.25, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.25, flexWrap: 'wrap' }}>
            {/* Truthfulness rule (carried into C.5a's real generalized
                scope): a Practice/Other's chip always names WHO it
                actually applies to — its one owning Team, every Team in
                the Division, or the specific Teams in event_team_scope —
                never a pretend single owner. */}
            {!isGame && (
              <Chip
                size="small"
                label={`${(EVENT_TYPE_LABELS[row.event_type] || 'Event').toUpperCase()} · ${describeEventScope(row, teamNamesById).toUpperCase()}`}
                sx={{ fontWeight: 700, bgcolor: '#0a2a14', color: '#2ea84b' }}
              />
            )}
            {row.kind === 'matchup' && (
              <Chip size="small" label="DIVISION" sx={{ fontWeight: 800, bgcolor: '#1a2e44', color: '#9eb8d0' }} />
            )}
            {/* Phase C.5b — a subtle "repeats" indicator, never a bigger
                card: this occurrence is one of a materialized series
                (044), not a one-off standalone event. */}
            {row.series_id && (
              <Chip size="small" label="↻ REPEATS" sx={{ fontWeight: 800, bgcolor: '#131b2a', color: '#6a8aa8' }} />
            )}
            {row.status !== 'scheduled' && (
              <Chip size="small" label={row.status.toUpperCase()} sx={{ fontWeight: 800, ...gameStatusChipStyle(row.status) }} />
            )}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', textDecoration: isCancelled ? 'line-through' : 'none' }}>
            {heading}
          </Typography>
          {isCompleted && result && (
            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: result === 'W' ? '#2ea84b' : result === 'L' ? '#f44336' : '#9eb8d0', mt: 0.25 }}>
              {result} {row.team_score}–{row.opponent_score}
            </Typography>
          )}
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mt: 0.25 }}>
            {formatGameDateCompact(row.event_date)}{row.start_time ? ` · ${formatTimeCompact(row.start_time)}` : ''}
          </Typography>
          {/* BROWSER ACCEPTANCE FOLLOW-UP (C.5c/C.5d) — private
              operational Notes (event or internal matchup alike) were
              previously only discoverable through Edit — never shown on
              this authenticated row at all. Safe to render unconditionally
              here: the public caller's own rows never carry `notes` in
              the first place (no public RPC selects it), so this is a
              no-op for a public viewer, not a separate gate to remember. */}
          {row.notes && (
            <Typography sx={{ color: '#4a6080', fontSize: '0.72rem', mt: 0.25 }}>{row.notes}</Typography>
          )}
        </Box>
        {canManage ? (
          <EventActionsMenu
            event={row} canManage={canManage} canDelete={canManage}
            onEdit={onEdit} onEnterResult={onComplete} onPostpone={onPostpone} onCancel={onCancel} onDelete={onDelete} onReopen={onReopen}
          />
        ) : href ? (
          <Button component={Link} to={href} size="small" sx={{ textTransform: 'none', fontWeight: 600, color: '#6a8aa8', fontSize: '0.72rem' }}>
            View Team →
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

// Phase C.5c — UP NEXT vs SCHEDULED no longer duplicate the same full
// management card twice (the exact same Game used to render in full,
// once highlighted up top and again under Scheduled below — pure wasted
// space with zero new information the second time). This is a compact,
// read-only summary row; the full actionable HubEventRow for the same
// Game still lives under SCHEDULED — nothing here removes or replaces
// the underlying scheduled event, only how it's shown twice.
function UpNextRow({ game, isLast }) {
  const heading = `${game.ownerTeamName} ${formatGameOpponentLine(game)}`;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
      py: 0.75, borderBottom: isLast ? 'none' : '1px solid #16202e',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.72rem', fontWeight: 800, flexShrink: 0 }}>
          {formatGameDateCompact(game.event_date)}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {heading}
        </Typography>
      </Box>
      {game.status === 'postponed' ? (
        <Chip size="small" label="POSTPONED" sx={{ fontWeight: 800, flexShrink: 0, ...gameStatusChipStyle('postponed') }} />
      ) : (
        <Typography sx={{ color: '#9eb8d0', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>
          {game.start_time ? formatTimeCompact(game.start_time) : ''}
        </Typography>
      )}
    </Box>
  );
}

// Phase C.5d — NEEDS RESULT. Closes the lifecycle audit's core
// discoverability finding: a Game whose date passed while still
// 'scheduled' matched none of Up Next/Scheduled/Results and simply
// vanished from this Hub. A compact row, matching UpNextRow's own
// density, with an "Enter Result" action for an authorized viewer and
// NO management control at all for a public one (the row's own DATA is
// already public — it's the same Game that would otherwise show as
// Upcoming — only the action is gated).
function NeedsResultRow({ game, canManage, onEnterResult, isLast }) {
  const heading = `${game.ownerTeamName} ${formatGameOpponentLine(game)}`;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
      py: 0.75, borderBottom: isLast ? 'none' : '1px solid #16202e',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.72rem', fontWeight: 800, flexShrink: 0 }}>
          {formatGameDateCompact(game.event_date)}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {heading}
        </Typography>
      </Box>
      {canManage && (
        <Button
          size="small" onClick={() => onEnterResult(game)}
          sx={{ textTransform: 'none', fontWeight: 700, color: '#2ea84b', minWidth: 0, flexShrink: 0 }}
        >
          Enter Result
        </Button>
      )}
    </Box>
  );
}

// Browser-found presentation fix — a neutral, two-line scoreboard ("Black
// 16 / Blue 12") rather than a single compressed, Team-perspective-like
// sentence. The Division's own record of a matchup, not "Black's Team
// page." Leaves visual room for a future third line ("Green BYE") once
// Week/Bye is actually built — not implemented this pass.
//
// Phase C.5c — the small "W" tag next to the winner's name is gone: bold
// weight + a slightly larger score already read as "winner" on their
// own, and W/L/T is explicitly a Team-record concept (see the TEAMS
// column), not something this neutral ledger needs to restate per row.
// BROWSER ACCEPTANCE FOLLOW-UP (C.5d.1) — a completed Result was
// previously display-only here: no way to Edit Result or Reopen Game
// from the one place a completed Game naturally lives. Reuses the same
// EventActionsMenu every other row already uses (`compact` — see its
// own header — folds "Edit Result"/"Reopen Game" into the "•••" menu
// with no separate inline button, so the card stays a two-line
// scoreboard, never a full Schedule row). `game` already carries `kind`/
// `id` from mergeDivisionSchedule()'s own normalization — routing to the
// correct event/matchup RPC is by that identity, never by opponent name.
// `canManage` is only ever true on the authenticated Hub; the public
// Division/Team page passes no action handlers at all and canManage is
// always false there, so EventActionsMenu's own `!canManage && !canDelete`
// guard returns null — zero management UI reaches a public viewer.
function ResultRow({ game, canManage, onEnterResult, onReopen }) {
  const result = deriveGameResult(game.team_score, game.opponent_score);
  const ownerWins = result === 'W';
  const opponentWins = result === 'L';
  return (
    <Box sx={{ py: 0.85, px: 1.25, backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', borderRadius: 2 }}>
      {(game.kind === 'matchup' || canManage) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
          {game.kind === 'matchup' ? (
            <Chip size="small" label="DIVISION" sx={{ fontWeight: 800, bgcolor: '#1a2e44', color: '#9eb8d0' }} />
          ) : <Box />}
          {canManage && (
            <EventActionsMenu
              event={game} canManage canDelete={false} compact
              onEdit={() => {}} onEnterResult={onEnterResult} onPostpone={() => {}} onCancel={() => {}} onDelete={() => {}} onReopen={onReopen}
            />
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: ownerWins ? 800 : 500, fontSize: '0.88rem' }}>{game.ownerTeamName}</Typography>
        <Typography sx={{ fontWeight: ownerWins ? 800 : 600, fontSize: '0.95rem' }}>{game.team_score}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.15 }}>
        <Typography sx={{ fontWeight: opponentWins ? 800 : 500, fontSize: '0.88rem' }}>{game.opponent_name}</Typography>
        <Typography sx={{ fontWeight: opponentWins ? 800 : 600, fontSize: '0.95rem' }}>{game.opponent_score}</Typography>
      </Box>
    </Box>
  );
}

// Browser-found density fix — thin separated rows instead of a full Card
// per Team; four Teams' worth of "Name  W-L" no longer needs to consume
// four oversized boxes of vertical space.
function TeamRecordRow({ team, record, href, isLast }) {
  const content = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.65 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{team.team_name}</Typography>
      <Typography sx={{ color: '#9eb8d0', fontSize: '0.8rem', fontWeight: 700 }}>{record.display || '—'}</Typography>
    </Box>
  );
  return (
    <Box sx={{ borderBottom: isLast ? 'none' : '1px solid #16202e' }}>
      {href ? <Link to={href} style={{ textDecoration: 'none', color: 'inherit' }}>{content}</Link> : content}
    </Box>
  );
}

export default function DivisionHub({
  division, perTeam, canManage, canManageRow, todayOverride, focusTeamId,
  onAddEvent, onEditRow, onCompleteRow, onPostponeRow, onReopenRow, onCancelRow, onDeleteRow, getTeamHref,
}) {
  const today = todayOverride || todayDateString();
  const rows = useMemo(() => mergeDivisionSchedule(perTeam), [perTeam]);
  // Phase C.5e — TEAM-FOCUSED SCHEDULE. `focusTeamId` is the one thing
  // that turns this same component into "Black's Schedule" instead of
  // the full Division view: Schedule/Needs-Result/Up-Next/Results are
  // filtered to what's relevant to that one Team (filterRowsForTeam() —
  // own Games, entire-Division/specific-Teams Practice that applies to
  // it, never an unrelated sibling's own Team-only Practice). The TEAMS/
  // records panel deliberately keeps using the FULL, unfiltered `rows`/
  // `perTeam` — Division competitive context stays Division-wide even
  // in Team-focused mode, per this phase's own explicit product rule.
  // Omitting focusTeamId (DivisionSchedulePage's/PublicDivisionPage's
  // own existing usage) leaves every section exactly as before —
  // filterRowsForTeam is never even called.
  const scopedRows = useMemo(
    () => (focusTeamId ? filterRowsForTeam(rows, focusTeamId) : rows),
    [rows, focusTeamId]
  );
  const upcoming = useMemo(() => deriveUpcomingGames(scopedRows, { today, limit: 5 }), [scopedRows, today]);
  const needsResult = useMemo(() => deriveNeedsResult(scopedRows, { today }), [scopedRows, today]);
  const scheduledGroups = useMemo(() => deriveScheduledByDate(scopedRows, { today }), [scopedRows, today]);
  const resultGroups = useMemo(() => deriveResultsByDate(scopedRows), [scopedRows]);
  const teamRecords = useMemo(
    () => sortTeamRecordsForDisplay(deriveDivisionTeamRecordsFromRows(perTeam, rows)),
    [perTeam, rows]
  );

  const teamNamesById = useMemo(
    () => Object.fromEntries((perTeam || []).map(({ team }) => [team.id, team.team_name])),
    [perTeam]
  );
  // Phase C.5e — `canManageRow` is an OPTIONAL per-row override of the
  // flat `canManage` above. DivisionSchedulePage.js/PublicDivisionPage.js
  // never pass it — reaching either page at all already implies uniform
  // authority over every row, so every existing caller keeps behaving
  // exactly as before. TeamSchedulePage.js's own Team-focused mode DOES
  // need this: a plain Team Head Coach's own `canManage` is true for
  // their own plain Team-scoped events, but the RPCs themselves
  // (cancel_team_event/delete_team_event/etc., 043/044) require
  // Program-level authority to manage an inherited entire-Division/
  // specific-Teams row — without this override, a management button
  // would render for a row the backend will actually reject, surfacing
  // a raw authorization error instead of never offering the control at
  // all. Never affects the TEAMS/records panel or the "+ Add Event"
  // button, which stay governed by the flat `canManage` (adding a new
  // event, or generally offering the workspace at all, isn't row-specific).
  const resolveCanManage = (row) => (canManageRow ? canManageRow(row) : canManage);
  const rowActionProps = {
    getTeamHref, teamNamesById,
    onEdit: onEditRow, onComplete: onCompleteRow, onPostpone: onPostponeRow, onReopen: onReopenRow, onCancel: onCancelRow, onDelete: onDeleteRow,
  };
  const secondaryLabel = divisionSecondaryLabel(division);

  return (
    <Box>
      {/* Browser-found fix — Season context: shown inline next to the
          Division name/type, small and muted rather than a dominant
          separate line (a Division page will otherwise become ambiguous
          the moment a 2027 Season exists alongside 2026). */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>{division.name}</Typography>
        <Chip size="small" label={competitionTypeLabel(division.competition_type)} sx={{ fontWeight: 700, ...competitionTypeChipStyle(division.competition_type) }} />
        {division.season_name && (
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', fontWeight: 700 }}>{division.season_name} Season</Typography>
        )}
      </Box>
      {/* Phase C.3.1's own anti-redundancy rule, carried into the shared
          Hub: only render this line when it's genuinely new information
          beyond the Division's own name already shown above. */}
      <Box sx={{ mb: 3 }}>
        {secondaryLabel && <Typography sx={{ color: '#9eb8d0' }}>{secondaryLabel}</Typography>}
      </Box>

      {/* Browser-found tightening — side rails narrower, center schedule
          dominant: this is the main operational surface. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '0.82fr 2.36fr 0.82fr' },
        gap: 3,
      }}>
        {/* RESULTS — left on desktop, last on mobile (a forward-looking
            visitor cares about what's coming up before what already
            happened). */}
        <Box sx={{ order: { xs: 3, md: 1 } }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#e8f0f8', mb: 1.5 }}>
            RESULTS
          </Typography>
          {resultGroups.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>No results yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {resultGroups.map(group => (
                <Box key={group.date}>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.72rem', fontWeight: 700, mb: 0.75 }}>
                    {formatResultDateLabel(group.date)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {group.games.map(g => (
                      <ResultRow key={`${g.kind}-${g.id}`} game={g} canManage={resolveCanManage(g)} onEnterResult={onCompleteRow} onReopen={onReopenRow} />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* CENTER — the main operational surface: Schedule header (+ Add
            Event lives HERE now, not up by Division identity — it's a
            schedule action, not a Team-management one), Upcoming Games,
            Scheduled. Widest column. */}
        <Box sx={{ order: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#e8f0f8' }}>
              SCHEDULE
            </Typography>
            {canManage && onAddEvent && (
              <Button variant="contained" color="primary" size="small" onClick={onAddEvent} sx={{ textTransform: 'none', fontWeight: 700 }}>
                + Add Event
              </Button>
            )}
          </Box>

          {/* Phase C.5d — NEEDS RESULT. Only rendered when non-empty — an
              alert-style section, not a routine one like the others,
              which always render (even with an empty-state message). */}
          {needsResult.length > 0 && (
            <>
              <Typography sx={{ fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#f5a623', mb: 1.5 }}>
                NEEDS RESULT
              </Typography>
              <Card sx={{ backgroundColor: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.35)', mb: 3 }}>
                <CardContent sx={{ py: 0.25, px: 1.5, '&:last-child': { pb: 0.25 } }}>
                  {needsResult.map((r, idx) => (
                    <NeedsResultRow
                      key={`nr-${r.kind}-${r.id}`} game={r} canManage={resolveCanManage(r)} onEnterResult={onCompleteRow}
                      isLast={idx === needsResult.length - 1}
                    />
                  ))}
                </CardContent>
              </Card>
            </>
          )}

          <Typography sx={{ fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#e8f0f8', mb: 1.5 }}>
            UP NEXT
          </Typography>
          {upcoming.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8', mb: 3 }}>No upcoming games scheduled.</Typography>
          ) : (
            <Card sx={{ backgroundColor: 'rgba(46,168,75,0.06)', border: '1px solid rgba(46,168,75,0.35)', mb: 3 }}>
              <CardContent sx={{ py: 0.25, px: 1.5, '&:last-child': { pb: 0.25 } }}>
                {upcoming.map((r, idx) => (
                  <UpNextRow key={`up-${r.kind}-${r.id}`} game={r} isLast={idx === upcoming.length - 1} />
                ))}
              </CardContent>
            </Card>
          )}

          <Typography sx={{ fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#e8f0f8', mb: 1.5 }}>
            SCHEDULED
          </Typography>
          {scheduledGroups.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8' }}>Nothing on the calendar yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {scheduledGroups.map(group => (
                <Box key={group.date}>
                  <Typography sx={{
                    fontSize: '0.72rem', fontWeight: 800, mb: 0.75, letterSpacing: '0.03em',
                    color: group.date === today ? '#2ea84b' : '#6a8aa8',
                  }}>
                    {formatScheduleDayLabel(group.date, today)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {group.events.map(r => <HubEventRow key={`sc-${r.kind}-${r.id}`} row={r} canManage={resolveCanManage(r)} {...rowActionProps} />)}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* TEAMS — each Team's derived SEASON RECORD (every completed Game
            it played, internal Division opponent or external/non-FFN
            opponent alike — see deriveDivisionTeamRecordsFromRows()'s own
            header for the canonical rule), sorted into a non-official
            display ordering (no tiebreaker rules exist yet) — never
            "STANDINGS", which is a separate, not-yet-built,
            competition-specific concept. Dense rows, not oversized
            cards. */}
        <Box sx={{ order: { xs: 2, md: 3 } }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#e8f0f8', mb: 1.5 }}>
            TEAMS
          </Typography>
          {teamRecords.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>No teams have been added to this division yet.</Typography>
          ) : (
            <Card sx={{ backgroundColor: 'rgba(10,20,34,0.6)', border: '1px solid #1e2a3a' }}>
              <CardContent sx={{ py: 0.5, px: 1.5, '&:last-child': { pb: 0.5 } }}>
                {teamRecords.map(({ team, record }, idx) => (
                  <TeamRecordRow
                    key={team.id} team={team} record={record}
                    href={getTeamHref ? getTeamHref(team) : null}
                    isLast={idx === teamRecords.length - 1}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
}
